import type { HolidayRegion } from '../calendar/holidays.js';
import { countWorkingDays } from '../calendar/working-days.js';
import type { StatusCategory } from '../projects.js';
import type { DateRange } from './constraints.js';

// Tasks with subtasks (GANTT-01, GANTT-08, decision 6b): their dates and progress are derived
// from the subtasks and stored by the API in the same transaction as the subtask change.

/** A task in a status of category `done` always counts as complete. */
export function effectiveProgress(progress: number, category: StatusCategory): number {
  return category === 'done' ? 100 : progress;
}

export interface SummaryChild {
  startDate: string | null;
  dueDate: string | null;
  progress: number;
  category: StatusCategory;
}

export interface Summary {
  /** Null if no subtask has a date: the parent keeps its own dates then. */
  range: DateRange | null;
  progress: number;
}

/**
 * Range from the earliest to the latest subtask date (a subtask with only one date counts as
 * that day). Progress is weighted by working days; unscheduled subtasks weigh one day.
 */
export function summarizeSubtasks(
  children: readonly SummaryChild[],
  region: HolidayRegion,
): Summary {
  let startDate: string | null = null;
  let dueDate: string | null = null;
  let weighted = 0;
  let weight = 0;
  for (const child of children) {
    const from = child.startDate ?? child.dueDate;
    const to = child.dueDate ?? child.startDate;
    let days = 1;
    if (from !== null && to !== null) {
      if (startDate === null || from < startDate) startDate = from;
      if (dueDate === null || to > dueDate) dueDate = to;
      days = Math.max(1, countWorkingDays(from, to, region));
    }
    weighted += effectiveProgress(child.progress, child.category) * days;
    weight += days;
  }
  return {
    range: startDate !== null && dueDate !== null ? { startDate, dueDate } : null,
    progress: weight > 0 ? Math.round(weighted / weight) : 0,
  };
}
